import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { StaffService } from './services/staff.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IconComponent } from '@app/components/shared/icon/icon.component';
import { IconName } from '@app/utils/enums/icon-enum';
import { OnboardingStatus } from '@app/utils/enums/view-onboarding';
import { ErrorModalComponent } from '@app/components/shared/error-modal/error-modal.component';
import { OnboardingSlide } from '@app/utils/interfaces/onboardingSlide.interface';

@Component({
  selector: 'app-onboarding-slides',
  standalone: true,
  imports: [TranslateModule, IconComponent],
  templateUrl: './onboarding-slides.component.html',
  styleUrl: './onboarding-slides.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OnboardingSlidesComponent implements OnInit {
  public readonly IconName = IconName;
  public readonly OnboardingStatus = OnboardingStatus;
  public slides = signal<OnboardingSlide[]>([]);
  public currentSlide = signal(0);

  private readonly translate = inject(TranslateService);
  private readonly dialogRef = inject(MatDialogRef<OnboardingSlidesComponent>);
  private readonly staffService = inject(StaffService);
  private readonly errDialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  public ngOnInit(): void {
    this.slides.set([
      {
        subtitle: this.translate.instant('onboardingSlides.slides.firstSlide.subtitle'),
        description: this.translate.instant('onboardingSlides.slides.firstSlide.description'),
        imageUrl: 'assets/images/onboarding-slide-1.jpg',
      },
      {
        subtitle: this.translate.instant('onboardingSlides.slides.secondSlide.subtitle'),
        description: this.translate.instant('onboardingSlides.slides.secondSlide.description'),
        imageUrl: 'assets/images/onboarding-slide-2.jpg',
      },
    ]);
  }

  public nextSlide(): void {
    if (this.currentSlide() < this.slides().length - 1) {
      this.currentSlide.update((value) => value + 1);
    }
  }

  public prevSlide(): void {
    if (this.currentSlide() > 0) {
      this.currentSlide.update((value) => value - 1);
    }
  }

  public closeModal(status: OnboardingStatus): void {
    const isCompleted = status === OnboardingStatus.completed;
    this.staffService
      .toggleOnboardingStatus(isCompleted)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.dialogRef.close(),
        error: (err) => {
          this.errDialog.open(ErrorModalComponent, {
            data: {
              message:
                err?.error?.message ||
                this.translate.instant('onboardingSlides.apiErrors.toggleStatus'),
            },
          });
          this.dialogRef.close();
        },
      });
  }
}
